// i18n
import { useTranslation } from 'react-i18next';

// react
import { MouseEvent, useContext, useState } from 'react';

// mui
import { Grid, IconButton } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// fields
import { Input } from '../../fields/Input/Input';

// types
import { ErrorCause, Feedback } from '../../../@types';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// icons
import { CloseOutlined as IconClose } from '@mui/icons-material';

// props
export interface FormAddExpenseTypeProps {
	onSuccess?: () => void;
}

export const FormAddExpenseType = ({ onSuccess }: FormAddExpenseTypeProps) => {
	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [values, setValues] = useState({ name: '' });

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { userEntity } = useContext(ContextUserEntity);

	// vars
	const formId = 'form-add-expense-type';

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const { name } = values;

		if (name) {
			try {
				// clear feedback
				setFeedback(null);

				setIsLoading(true);

				const body = {
					entity: userEntity?.id,
					name,
					project: project?.id,
				};

				const fetchExpenseType = await fetch(`${process.env.REACT_APP_API_URL}/expense-types`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify(body),
				});
				const resAddExpenseType = await fetchExpenseType.json();

				if (resAddExpenseType.error) {
					throw new Error(resAddExpenseType.error.message, {
						cause: {
							id: resAddExpenseType.error.id,
						},
					});
				}

				if (onSuccess) {
					onSuccess();
				}

				// clear form
				setValues({
					name: '',
				});

				// set success banner
				setBanner({
					hasClose: true,
					message: t('alert.expenseTypeCreated', { name }),
					severity: 'success',
				});
			} catch (error) {
				const err = error as Error;
				const cause = err.cause as ErrorCause;

				const feedbackNew: Feedback = {
					alert: {
						children: t('error.default'),
					},
				};

				if (cause.id === 'EXPENSE_TYPE_DUPLICATE') {
					feedbackNew.alert.children = t('error.expenseTypeDuplicate', { name });
				}

				if (cause.id === 'EXPENSE_TYPE_NAME_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.expenseTypeNameNonExistent');
				}

				if (cause.id === 'EXPENSE_TYPE_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				}

				// set feedback
				setFeedback({
					...feedbackNew,
					alert: {
						...feedbackNew.alert,
						action: (
							<IconButton color="inherit" onClick={() => setFeedback(null)} size="small">
								<IconClose />
							</IconButton>
						),
						sx: {
							...feedbackNew.alert.sx,
							alignItems: 'center',
						},
					},
				});
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<form id={formId} name="formAddExpenseType" onSubmit={handleSubmit}>
			<Grid container spacing={2} item xs={12}>
				<Grid item xs={12} md={true}>
					<Input
						aria-label={t('addExpenseType')}
						disabled={isLoading}
						fullWidth
						id={`${formId}-name`}
						name={'name'}
						onChange={(e) => setValues({ ...values, name: e.target.value })}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md="auto">
					<LoadingButton
						disabled={isLoading}
						fullWidth={true}
						loading={isLoading}
						type="submit"
						variant="outlined">
						{t('addExpenseType')}
					</LoadingButton>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback feedback={feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
